import styles from './SalesOpportunitiesRatingStatusList.module.scss';

import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton } from '@mui/material';
import { noop } from 'lodash';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { useSalesOpportunityRatingStatus } from '@work4all/components/lib/components/entity-picker/sales-opportunities-rating-status-picker/components/use-sales-opportunity-rating-status';

import { useDataMutation, useDeleteEntity } from '@work4all/data';

import { SalesOpportunityRatingStatus } from '@work4all/models/lib/Classes/SalesOpportunityRatingStatus.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Collapse } from '../../../../components';
import { EditTable } from '../../../erp/components/tab-panels/positions/components/edit-table/EditTable';
import { useEditableState } from '../../../erp/components/tab-panels/positions/components/edit-table/hooks/use-editable-state';
import {
  EditTableColumns,
  OnEditPosition,
} from '../../../erp/components/tab-panels/positions/components/edit-table/types';
import { useSelectedPositionState } from '../../../erp/components/tab-panels/positions/components/hooks/use-selected-position';
import { RemovePositionButton } from '../../../erp/components/tab-panels/positions/components/remove-position-button/RemovePositionButton';

const prepareRequest = (
  result: OnEditPosition<SalesOpportunityRatingStatus>
) => {
  const { position } = result;
  const request: Partial<SalesOpportunityRatingStatus> = {};
  request.id = position.id;

  Object.entries(position).forEach((x) => {
    const [field, value] = x;
    if (field === 'ratingValue') {
      request[field] = Math.max(0, Math.min(100, parseFloat(`${value}`)));
    } else request[field] = value;
  });

  return request;
};

export const SalesOpportunitiesRatingStatusList = () => {
  const { t } = useTranslation();
  const { data, refetch } = useSalesOpportunityRatingStatus();

  const [deleteEntity] = useDeleteEntity(false);

  const [mutate] = useDataMutation<SalesOpportunityRatingStatus, EMode.upsert>({
    entity: Entities.salesOpportunityRatingStatus,
    mutationType: EMode.upsert,
    onCompleted: () => {
      refetch();
    },
    responseData: { id: null },
  });

  const callbacks = useMemo(() => {
    const onAddPosition = () => {
      mutate({ name: '', rating: 0 });
    };

    const onEditPosition = (
      result: OnEditPosition<SalesOpportunityRatingStatus>
    ) => {
      const request = prepareRequest(result);
      // avoid sending id only
      if (!Object.keys(request).filter((x) => x !== 'id').length) return;
      mutate(request);
    };

    const onRemovePosition = async (ids: string[]) => {
      try {
        await deleteEntity({
          ids: ids,
          type: Entities.salesOpportunityRatingStatus,
        });
      } finally {
        refetch();
      }
    };

    return {
      onAddPosition,
      onEditPosition,
      onMovePosition: noop,
      onRemovePosition,
    };
  }, [mutate, deleteEntity, refetch]);

  const { positions: items, ...localCallbacks } =
    useEditableState<SalesOpportunityRatingStatus>({
      positions: data,
      ...callbacks,
    });

  const columns = useMemo(() => {
    const columns: EditTableColumns<SalesOpportunityRatingStatus>[] = [
      {
        Header: t('INPUTS.VALUE'),
        accessor: 'ratingValue',
        width: 80,
        Cell: (props) => `${props.value} %`,
        type: 'number',
        unit: '%',
        min: 0,
        step: 10,
        max: 100,
        transform: (input: number) =>
          Math.min(Math.max(Math.round(input / 10) * 10, 0), 100),
      },
      {
        Header: t('INPUTS.DESCRIPTION'),
        accessor: 'name',
        width: 160,
        type: 'text',
      },
      {
        Header: t('COMMON.COMMENT'),
        accessor: 'comment',
        type: 'text',
        width: 280,
      },
    ];
    return columns;
  }, [t]);

  const tableInstanceRef = useRef<TableInstance>();
  const { setSelectedPositionIds } = useSelectedPositionState();

  return (
    <Collapse
      title={t('COMMON.RATED_STATUS')}
      defaultOpen={true}
      className={styles['collapse-indent']}
      collapseContainerStyles={styles.collapse}
      classes={{
        wrapper: styles['collapse-wrapper'],
        wrapperInner: styles['collapse-wrapper-inner'],
      }}
      headerBar={
        <Box width="100%" display="flex" justifyContent="flex-end">
          <IconButton
            color="primary"
            onClick={() => {
              localCallbacks.onAddPosition();
            }}
          >
            <AddIcon />
          </IconButton>
          <RemovePositionButton
            onRemovePosition={localCallbacks.onRemovePosition}
          />
        </Box>
      }
    >
      <EditTable<SalesOpportunityRatingStatus>
        {...localCallbacks}
        tableInstanceRef={tableInstanceRef}
        items={items}
        onSelectedItemIdsChange={setSelectedPositionIds}
        columns={columns}
        classes={{
          tbody: styles['no-scroll'],
          table: styles.table,
        }}
        fitSpace
      />
    </Collapse>
  );
};
